import { useMutation, useQueryCache } from "react-query";
import { useToasts } from "react-toast-notifications";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import {
  PutNotifySubscriberContactMethodBody,
  PutNotifySubscriberContactMethodResponse,
  putNotifySubscriberContactMethod,
} from "../../../queries/notify";
import { buildNotifyInfiniteSubscriberContactMethodsQueryKey } from "./useInfiniteNotifySubscriberContactMethods";
import { buildNotifySubscriberContactMethodsQueryKey } from "./useNotifySubscriberContactMethodsForLoggedInUser";

export function useNotifySubscriberContactMethodUpsert({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (subscriberContactMethod: PutNotifySubscriberContactMethodResponse) => void | Promise<void>;
  onError?: () => void;
} = {}) {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const { addToast } = useToasts();

  const [upsertSubscriberContactMethodMutation, { isLoading: isUpsertSubscriberContactMethodLoading }] = useMutation(
    putNotifySubscriberContactMethod(networkHeaders),
    {
      onSuccess: async (subscriberContactMethod) => {
        await queryCache.invalidateQueries(buildNotifyInfiniteSubscriberContactMethodsQueryKey());
        await queryCache.invalidateQueries(buildNotifySubscriberContactMethodsQueryKey());
        onSuccess(subscriberContactMethod);
      },
      onError: () => {
        addToast("Failed to create subscriber contact method.", { appearance: "error", autoDismiss: true });
        onError();
      },
    },
  );

  const upsertSubscriberContactMethod = async ({
    identifier,
    identifierOrigin,
    identifierType,
    personId,
    smsCapable,
    otp,
  }: PutNotifySubscriberContactMethodBody) => {
    return await upsertSubscriberContactMethodMutation({
      identifier,
      identifierOrigin,
      identifierType,
      personId,
      smsCapable,
      otp,
    });
  };

  return {
    upsertSubscriberContactMethod,
    isUpsertSubscriberContactMethodLoading: isUpsertSubscriberContactMethodLoading,
  };
}
